import {formatNumber} from '@angular/common';
import {Component, ElementRef, inject, LOCALE_ID, NgZone, OnDestroy, ViewChild} from '@angular/core';
import {ImportModuleTableStats} from '../../../core/definitions/import-module';
import {ImportSessionStore} from '../import-session.store';
import {lookupDisplayName} from '../lookup-controls';
import {rowCountText} from '../row-count';

/** The fewest of the selected table's columns its preview shows. */
export const PREVIEW_COLUMNS = 6;
/** The width a preview column is given when working out how many fit. */
export const PREVIEW_COLUMN_WIDTH = 150;

/** The format a source file's extension names, as the file card shows it.
 * Its keys are also the extensions the file picker offers. */
const FILE_FORMATS: {[extension: string]: string} = {
  csv: 'CSV',
  xlsx: 'Excel',
  accdb: 'Access',
  mdb: 'Access',
};
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

/** An open option list of a concept field in the batch defaults. The
 * shared `edit-field-select` renders its list as `.show-options` only while
 * it is open; the step finds the list by that class, so it has to follow a
 * rename there. */
export const OPEN_OPTION_LIST_SELECTOR = '.show-options';
/** The Import module page, and its step footer pinned over the bottom of
 * the scroll area. */
const PAGE_SELECTOR = '.admin-import-module';
const STEP_FOOTER_SELECTOR = '.admin-import-module__step-footer';

/** The format of `fileName` by its extension; `null` for an extension the
 * page does not take. */
export function fileFormat(fileName: string): string | null {
  const dot = fileName.lastIndexOf('.');
  return dot < 0 ? null : FILE_FORMATS[fileName.slice(dot + 1).toLowerCase()] ?? null;
}

/** `bytes` in the largest unit (of 1024) that keeps the number at 1 or
 * more, with at most one decimal in `locale`: "2,1 MB" in nb. */
export function fileSizeText(bytes: number, locale: string): string {
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    size /= 1024;
    unit++;
  }
  return `${formatNumber(size, locale, unit ? '1.0-1' : '1.0-0')} ${FILE_SIZE_UNITS[unit]}`;
}

/** One table of the table picker. `stats` is missing until the counts
 * arrive. */
export interface SourceTableRow {
  name: string;
  stats: ImportModuleTableStats | undefined;
  // Counted, and has no rows: it cannot be picked.
  empty: boolean;
  // Has the most rows of the file's tables.
  largest: boolean;
  // The names of the lookups that read this table.
  lookupNames: string[];
}

/**
 * The Import module's Kilde step: the resume offer, the source file, the
 * table with its counts and a preview of its first rows, the object type
 * with its batch defaults, and the media link.
 */
@Component({
  selector: 'app-import-source-step',
  templateUrl: './import-source-step.component.html',
  styleUrls: ['./import-source-step.component.scss'],
  standalone: false,
})
export class ImportSourceStepComponent implements OnDestroy {
  readonly store: ImportSessionStore = inject(ImportSessionStore);
  private readonly zone: NgZone = inject(NgZone);
  private readonly locale: string = inject(LOCALE_ID);

  readonly acceptedExtensions = Object.keys(FILE_FORMATS).map(extension => '.' + extension).join(',');

  /** How many columns the preview shows: as many as fit its width at
   * `PREVIEW_COLUMN_WIDTH` each, and never fewer than `PREVIEW_COLUMNS`. */
  previewColumnCount = PREVIEW_COLUMNS;
  private previewObserver?: ResizeObserver;

  @ViewChild('preview') set previewElement(preview: ElementRef<HTMLElement> | undefined) {
    this.previewObserver?.disconnect();
    this.previewObserver = undefined;
    if (!preview || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.previewObserver = new ResizeObserver(entries => {
      const count = Math.max(PREVIEW_COLUMNS, Math.floor(entries[0].contentRect.width / PREVIEW_COLUMN_WIDTH));
      if (count !== this.previewColumnCount) {
        this.zone.run(() => this.previewColumnCount = count);
      }
    });
    this.previewObserver.observe(preview.nativeElement);
  }

  private batchDefaultsObserver?: MutationObserver;
  private optionListObserver?: ResizeObserver;
  private optionList: HTMLElement | null = null;

  /** Watches the batch defaults for a field's option list opening, which
   * the shared field component renders on its own time. */
  @ViewChild('batchDefaults') set batchDefaultsElement(block: ElementRef<HTMLElement> | undefined) {
    this.batchDefaultsObserver?.disconnect();
    this.batchDefaultsObserver = undefined;
    this.followOptionList(null);
    if (!block) {
      return;
    }
    const element = block.nativeElement;
    this.zone.runOutsideAngular(() => {
      this.batchDefaultsObserver = new MutationObserver(() =>
        this.followOptionList(element.querySelector<HTMLElement>(OPEN_OPTION_LIST_SELECTOR)));
      this.batchDefaultsObserver.observe(element, {childList: true, subtree: true});
    });
  }

  ngOnDestroy(): void {
    this.previewObserver?.disconnect();
    this.batchDefaultsObserver?.disconnect();
    this.optionListObserver?.disconnect();
  }

  /** Scrolls the page just far enough to show `list` above the pinned step
   * footer: when it opens, and again when its height changes as its options
   * arrive. */
  private followOptionList(list: HTMLElement | null): void {
    if (list === this.optionList) {
      return;
    }
    this.optionList = list;
    this.optionListObserver?.disconnect();
    this.optionListObserver = undefined;
    if (!list) {
      return;
    }
    const bringIntoView = (): void => {
      const footer = list.closest(PAGE_SELECTOR)?.querySelector(STEP_FOOTER_SELECTOR);
      list.style.scrollMarginBottom = `${footer?.getBoundingClientRect().height ?? 0}px`;
      list.scrollIntoView({block: 'nearest'});
    };
    bringIntoView();
    if (typeof ResizeObserver !== 'undefined') {
      this.optionListObserver = new ResizeObserver(bringIntoView);
      this.optionListObserver.observe(list);
    }
  }

  /** The file card's facts before its table count: the format, and the
   * size where the store has it. */
  get fileFacts(): string[] {
    const format = fileFormat(this.store.fileName);
    const size = this.store.fileSize;
    return [
      ...(format ? [format] : []),
      ...(size !== null ? [fileSizeText(size, this.locale)] : []),
    ];
  }

  /** The file's tables as the picker lists them: the counted ones with
   * rows, most rows first, then those not counted yet in file order, then
   * the empty ones. */
  get tableRows(): SourceTableRow[] {
    const stats = this.store.tableStats;
    const mostRows = Math.max(0, ...this.store.tables.map(name => stats.get(name)?.rows ?? 0));
    const largest = this.store.tables.length > 1 && mostRows > 0
      ? this.store.tables.find(name => stats.get(name)?.rows === mostRows)
      : undefined;
    const rank = (name: string): number => {
      const rows = stats.get(name)?.rows;
      return rows === undefined ? 0 : rows === 0 ? 1 : -rows;
    };
    const ordered = this.store.tables
      .map((name, index) => ({name, index}))
      .sort((a, b) => rank(a.name) - rank(b.name) || a.index - b.index)
      .map(({name}) => name);
    return ordered.map(name => ({
      name,
      stats: stats.get(name),
      empty: stats.get(name)?.rows === 0,
      largest: name === largest,
      lookupNames: this.store.lookupDefinitions
        .filter(lookup => lookup.tableControl.value === name)
        .map(lookupDisplayName),
    }));
  }

  /** The selected table's columns the preview shows. */
  get previewColumns(): string[] {
    return this.store.columns.slice(0, this.previewColumnCount).map(column => column.name);
  }

  /** The selected table's columns the preview leaves out. */
  get otherColumns(): string[] {
    return this.store.columns.slice(this.previewColumnCount).map(column => column.name);
  }

  /** The selected table's counts, once they have arrived. */
  get selectedTableStats(): ImportModuleTableStats | undefined {
    return this.store.tableStats.get(this.store.selectedTable);
  }

  /** A table's row count, marked as approximate where the format's count
   * can overshoot. */
  rowCount(stats: ImportModuleTableStats): string {
    return rowCountText({rows: stats.rows, exact: stats.rows_exact});
  }

  /** Uploads the picked file, and empties the input so that picking the
   * same file again is a change too. */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      void this.store.uploadSource(file);
    }
  }
}
